import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";

export type NullableString = string | null;
export type NullableBoolean = boolean | null;

export interface EmploymentStatus {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	code: string;
	name: string;
	description: string;
}

export interface EmploymentStatusResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: EmploymentStatus[];
}

export const fetchEmploymentStatuses = async (): Promise<EmploymentStatusResponse> => {
	try {
		const response = await api.get(SERVER_ROUTES.INSTITUTES.EMPLOYMENT_STATUS);
		return response as unknown as EmploymentStatusResponse;
	} catch (error) {
		console.error("Error fetching employment statuses:", error);
		throw new Error("Failed to fetch employment statuses. Please try again.");
	}
};

export interface EmploymentActivity {
	id?: number;
	uuid?: string;
	activityType?: string;
	type?: string;
	organizationName: string;
	roleTitle: string;
	monthlyIncome: number;
	salary?: number | null;
	contactPersonName?: string;
	contactPersonNumber?: string;
	contactPersonEmail?: string;
	startDate: string;
	endDate?: NullableString;
	jobStarted?: NullableString;
	jobEnded?: NullableString;
	employerBusinessDetailsId?: string | number | null;
	employerBusinessDetails?: EmploymentEmployer | null;
	outcomeJobType?: EmploymentOutcomeJobType | null;
	country?: EmploymentLocation | null;
	district?: EmploymentLocation | null;
	city?: EmploymentLocation | null;
	createdAt?: string;
	updatedAt?: string;
}

export interface EmploymentEmployer {
	id?: number;
	uuid: string;
	businessName: string;
}

export interface EmploymentOutcomeJobType {
	uuid: string;
	name: string;
}

export interface EmploymentLocation {
	id?: number;
	uuid: string;
	name: string;
}

export interface StudentEmploymentPayload {
	type: "EMPLOYMENT" | "SELF_EMPLOYMENT";
	outcomeStatusUuid: string;
	outcomeJobTypeUuid: string;
	employerBusinessDetailsId: string;
	organizationName: string;
	contactPersonName?: string;
	contactPersonNumber?: string;
	contactPersonEmail?: string;
	roleTitle: string;
	monthlyIncome: number;
	startDate: string;
	endDate?: string;
	countryUuid?: string;
	districtUuid?: string;
	cityUuid?: string;
}

export type EmploymentActivityUpsertPayload = StudentEmploymentPayload;

export interface EmploymentActivityResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: EmploymentActivity;
}

export interface EmploymentActivityListResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: EmploymentActivity[];
}

export const fetchEmploymentActivity = async (activityType?: string): Promise<EmploymentActivityResponse> => {
	try {
		const response = await api.get(SERVER_ROUTES.STUDENT.EMPLOYMENTS, {
			params: activityType ? { activityType } : undefined,
		});
		return response as unknown as EmploymentActivityResponse;
	} catch (error) {
		console.error("Error fetching employment activity:", error);
		throw new Error("Failed to fetch employment activity. Please try again.");
	}
};

export const createEmploymentActivity = async (
	payload: EmploymentActivityUpsertPayload,
): Promise<EmploymentActivityResponse> => {
	try {
		const response = await api.post(SERVER_ROUTES.STUDENT.EMPLOYMENTS, payload);
		return response as unknown as EmploymentActivityResponse;
	} catch (error) {
		console.error("Error creating employment activity:", error);
		throw new Error("Failed to create employment activity. Please try again.");
	}
};

export const updateEmploymentActivity = async (
	uuid: string,
	payload: EmploymentActivityUpsertPayload,
): Promise<EmploymentActivityResponse> => {
	try {
		const response = await api.put(SERVER_ROUTES.STUDENT.EMPLOYMENT_BY_UUID(uuid), payload);
		return response as unknown as EmploymentActivityResponse;
	} catch (error) {
		console.error("Error updating employment activity:", error);
		throw new Error("Failed to update employment activity. Please try again.");
	}
};

export const fetchEmploymentActivityById = async (uuid: string): Promise<EmploymentActivityResponse> => {
	try {
		const response = await api.get(SERVER_ROUTES.STUDENT.EMPLOYMENT_BY_UUID(uuid));
		return response as unknown as EmploymentActivityResponse;
	} catch (error) {
		console.error("Error fetching employment activity by id:", error);
		throw new Error("Failed to fetch employment activity. Please try again.");
	}
};

export interface Employer {
	id: number;
	uuid: string;
	businessName: string;
}

export interface OutcomeJobType {
	id: number;
	uuid: string;
	name: string;
}

export interface LocationOption {
	id: number;
	uuid: string;
	name: string;
}

export interface PaginatedListResponse<T> {
	data: T[];
	pagination?: {
		currentPage: number;
		perPage: number;
		totalItems: number;
		totalPages: number;
		hasNextPage: boolean;
		hasPreviousPage: boolean;
	};
}

export const fetchEmployers = async (params: { page?: number; limit?: number; search?: string }) => {
	const response = await api.get(SERVER_ROUTES.INSTITUTES.EMPLOYERS, { params });
	return response;
};

export const fetchOutcomeJobTypes = async (params: {
	page?: number;
	limit?: number;
	search?: string;
	outcomeStatusUuid: string;
}) => {
	const response = await api.get(SERVER_ROUTES.INSTITUTES.OUTCOME_JOB_TYPES, { params });
	return response;
};

export const fetchCountries = async (params: { page?: number; limit?: number; search?: string }) => {
	const response = await api.get(SERVER_ROUTES.INSTITUTES.COUNTRY, { params });
	return response;
};

export const fetchDistricts = async (params: { page?: number; limit?: number; search?: string }) => {
	const response = await api.get(SERVER_ROUTES.INSTITUTES.DISTRICT, { params });
	return response;
};

export const fetchCitiesByDistrict = async (
	districtId: string,
	params: { page?: number; limit?: number; search?: string },
) => {
	const response = await api.get(`${SERVER_ROUTES.INSTITUTES.CITY}/district/${districtId}/cities`, { params });
	return response;
};

export interface StudentOutcomeStatus {
	id: number;
	uuid: string;
	code: string;
	name: string;
	description?: string;
}

export interface StudentOutcomeCurrent {
	id: number;
	uuid: string;
	studentId: number;
	outcomeStatusId: number;
	outcomeStatus: StudentOutcomeStatus;
	effectiveFrom?: NullableString;
	isVerified?: NullableBoolean;
	remarks?: NullableString;
	createdAt?: string;
	updatedAt?: string;
}

export interface StudentOutcomeCurrentResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: StudentOutcomeCurrent | null;
}

export const fetchStudentOutcomeCurrent = async (): Promise<StudentOutcomeCurrentResponse> => {
	try {
		const response = await api.get(SERVER_ROUTES.STUDENT.OUTCOME_CURRENT);
		return response as unknown as StudentOutcomeCurrentResponse;
	} catch (error) {
		console.error("Error fetching current student outcome:", error);
		throw new Error("Failed to fetch current student outcome. Please try again.");
	}
};

export interface HigherStudy {
	id?: number;
	uuid?: string;
	instituteName: string;
	contactPersonName: string;
	contactPersonNumber: string;
	degreeName: string;
	createdAt?: string;
	updatedAt?: string;
}

export type HigherStudyUpsertPayload = Omit<HigherStudy, "id" | "uuid" | "createdAt" | "updatedAt">;

export interface HigherStudyResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: HigherStudy | null;
}

export const fetchHigherStudy = async (): Promise<HigherStudyResponse> => {
	try {
		const response = await api.get(SERVER_ROUTES.INSTITUTES.HIGHER_STUDY);
		return response as unknown as HigherStudyResponse;
	} catch (error) {
		console.error("Error fetching higher study:", error);
		throw new Error("Failed to fetch higher study. Please try again.");
	}
};

export const createHigherStudy = async (
	payload: HigherStudyUpsertPayload,
): Promise<HigherStudyResponse> => {
	try {
		const response = await api.post(SERVER_ROUTES.INSTITUTES.HIGHER_STUDY, payload);
		return response as unknown as HigherStudyResponse;
	} catch (error) {
		console.error("Error creating higher study:", error);
		throw new Error("Failed to create higher study. Please try again.");
	}
};

export const updateHigherStudy = async (
	uuid: string,
	payload: HigherStudyUpsertPayload,
): Promise<HigherStudyResponse> => {
	try {
		const response = await api.put(SERVER_ROUTES.INSTITUTES.HIGHER_STUDY_BY_UUID(uuid), payload);
		return response as unknown as HigherStudyResponse;
	} catch (error) {
		console.error("Error updating higher study:", error);
		throw new Error("Failed to update higher study. Please try again.");
	}
};
